import React, { FC } from 'react'
import { Trans, t } from '@lingui/macro'
import {
  useRemoveGroupMutation,
  useGetGroupRelatedCampaignsQuery
} from './GroupRemoveModal.codegen'
import { CampaignStateEnum } from '../../../graphql/private/PrivateSchemaTypes'

import Loader from '../../../components/common/loader/Loader'
import {
  ButtonDanger,
  ButtonClean,
  Text,
  Modal
} from 'front-end/src/components'
import { notification } from '../../../components/common/notifications/Notifications'
import { ResourceRelatedInstances } from './ResourceRelatedInstances'

type GroupDeleteModalProps = {
  groupId: number
  show: boolean
  onCanceled: () => void
  onDeleted: () => void
}

export const GroupDeleteModal: FC<GroupDeleteModalProps> = ({
  onCanceled,
  show,
  groupId,
  onDeleted
}) => {
  const [removeGroupMutation, { loading: isRemovingInProgress }] =
    useRemoveGroupMutation({
      onError: () => {
        notification.error(t`Error deleting group`)
      },
      onCompleted: () => {
        notification.info(t`Group has been deleted`)
      }
    })
  const { loading, data } = useGetGroupRelatedCampaignsQuery({
    variables: {
      groupId
    }
  })

  const handleOnDelete = async (): Promise<void> => {
    await removeGroupMutation({
      variables: { id: groupId }
    })
    onDeleted()
  }

  const getControls = () => {
    return (
      <>
        <ButtonClean onClick={onCanceled}>
          <Trans>Cancel</Trans>
        </ButtonClean>
        <ButtonDanger
          isDisabled={loading}
          isLoading={isRemovingInProgress}
          onClick={handleOnDelete}
          dataTest="DeleteGroupModalButton"
        >
          <Trans>Delete this group</Trans>
        </ButtonDanger>
      </>
    )
  }

  const relatedCampaigns = data?.admin?.group?.relatedCampaigns.filter(
    (campaign) => campaign.status !== CampaignStateEnum.DRAFT
  )

  return (
    <Modal
      header={
        <Text align="left" pb="8px">
          <Trans>Are you sure you want to delete this group?</Trans>
        </Text>
      }
      show={show}
      controls={getControls}
      onHide={onCanceled}
    >
      {loading && <Loader />}
      {!loading && relatedCampaigns && relatedCampaigns?.length > 0 && (
        <>
          <Text align="left" pb="8px">
            <Trans>
              This group is used in the audience criteria of the following
              campaigns. Deleting this group will update campaigns with multiple
              groups, and will stop campaigns with this group only.
            </Trans>
          </Text>
          <ResourceRelatedInstances campaigns={relatedCampaigns} />
        </>
      )}
      {!loading && relatedCampaigns && !relatedCampaigns?.length && (
        <Text align="left" pb="8px">
          <Trans>Users inside this group will not be affected.</Trans>
        </Text>
      )}
    </Modal>
  )
}
